import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import SectionEditForm from './SectionEditForm';
import HoursControl from '../../HoursControl';
import client from '../../../lib/feathers';
import { setListing } from '../../../lib/listingsSlice';

/**
 * Opening hours, on their own.
 *
 * Hours are the field a winery is most likely to find wrong — they change with
 * the season and no crawler keeps up — so this is the checklist item most worth
 * making easy to reach. PATCHes `hours` and nothing else.
 */

// Week starts Saturday, matching the existing editor.
const DAY_ORDER = [6, 0, 1, 2, 3, 4, 5];
const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const STATUSES = [
  { value: 'Normal', label: 'Operating normally', hint: 'Open with regular business hours.' },
  { value: 'By Appointment Only', label: 'By appointment only', hint: 'Open, but visits require an appointment — no set hours.' },
  { value: 'Temporarily Closed', label: 'Temporarily closed', hint: 'Closed, but intending to open again.' },
  { value: 'Permanently Closed', label: 'Permanently closed', hint: 'Closed for good.' },
];

const emptyHours = () => ({ status: { value: 'Normal', message: '' }, days: {} });

export default function HoursEditForm() {
  const { lid } = useParams();
  const dispatch = useDispatch();
  const listing = useSelector((s) => s.listings.selected);
  const currentUser = useSelector((s) => s.auth.user);
  const role = currentUser?.role === 'admin' ? 'admin' : 'industry';
  const [hours, setHours] = useState(emptyHours());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let row = listing?.id === lid ? listing : null;
      if (!row) {
        try {
          row = await client.service(`${role}/listing`).get(lid);
          if (cancelled) return;
          dispatch(setListing(row));
        } catch (e) {
          console.log('could not load listing', e);
          return;
        }
      }
      const existing = row.hours || {};
      setHours({
        status: { value: 'Normal', message: '', ...(existing.status || {}) },
        days: existing.days || {},
      });
    })();
    return () => { cancelled = true; };
  }, [lid, listing?.id, dispatch]);

  if (!listing || listing.id !== lid) return null;

  const setStatus = (value) => setHours((h) => ({ ...h, status: { ...h.status, value } }));
  const setMessage = (message) => setHours((h) => ({ ...h, status: { ...h.status, message } }));

  const dayOf = (h, dayIndex) => h.days[dayIndex] || { hours: [] };

  const addRange = (dayIndex) => setHours((h) => {
    const day = dayOf(h, dayIndex);
    return {
      ...h,
      days: { ...h.days, [dayIndex]: { ...day, closed: false, hours: [...(day.hours || []), {}] } },
    };
  });

  const removeRange = (dayIndex, hourIndex) => setHours((h) => {
    const day = dayOf(h, dayIndex);
    const next = [...(day.hours || [])];
    next.splice(hourIndex, 1);
    return { ...h, days: { ...h.days, [dayIndex]: { ...day, hours: next } } };
  });

  const changeRange = (dayIndex, hourIndex, value) => setHours((h) => {
    const day = dayOf(h, dayIndex);
    const next = [...(day.hours || [])];
    next[hourIndex] = value;
    return { ...h, days: { ...h.days, [dayIndex]: { ...day, hours: next } } };
  });

  const setClosed = (dayIndex, closed) => setHours((h) => {
    const day = dayOf(h, dayIndex);
    return { ...h, days: { ...h.days, [dayIndex]: { ...day, closed } } };
  });

  const showsDays = hours.status.value === 'Normal';

  return (
    <SectionEditForm
      listing={listing}
      title="Opening hours"
      description="When visitors can turn up. This changes with the season more than anything else on your listing, so it is worth a look."
      backTo={`/${role}/listings/${lid}`}
      buildPatch={() => ({ hours })}
      onSaved={(updated) => dispatch(setListing({ ...listing, hours: updated.hours ?? hours }))}
    >
      <fieldset>
        <legend className="text-sm font-medium text-gray-700">Are you open?</legend>
        <div className="mt-3 space-y-3">
          {STATUSES.map((s) => (
            <label key={s.value} className="flex items-start cursor-pointer">
              <input
                type="radio"
                name="availability"
                value={s.value}
                checked={hours.status.value === s.value}
                onChange={() => setStatus(s.value)}
                className="mt-1 h-4 w-4 text-red-800 border-gray-300 focus:ring-red-800"
              />
              <span className="ml-3 text-sm">
                <span className="font-medium text-gray-700">{s.label}</span>
                <span className="block text-gray-500">{s.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {hours.status.value !== 'Normal' && (
        <div>
          <label htmlFor="statusMessage" className="block text-sm font-medium text-gray-700">
            Anything visitors should know?
          </label>
          <textarea
            id="statusMessage"
            rows={2}
            value={hours.status.message || ''}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={hours.status.value === 'By Appointment Only'
              ? 'Tell visitors how to book.'
              : 'Optional — when you expect to reopen, for instance.'}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-800 focus:ring-red-800 sm:text-sm"
          />
        </div>
      )}

      {showsDays && (
        <div className="pt-2">
          <h4 className="text-sm font-medium text-gray-900">Hours for each day</h4>
          <div className="mt-3 space-y-4">
            {DAY_ORDER.map((dayIndex) => {
              const day = dayOf(hours, dayIndex);
              const ranges = day.hours || [];
              return (
                <div key={dayIndex} className="pb-4 border-b border-gray-200 last:border-b-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">{DAY_LABELS[dayIndex]}</span>
                    <button
                      type="button"
                      onClick={() => addRange(dayIndex)}
                      className="text-sm text-red-800 hover:underline"
                    >
                      + Add hours
                    </button>
                  </div>

                  {ranges.length === 0 && (
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={day.closed || false}
                        onChange={(e) => setClosed(dayIndex, e.target.checked)}
                        className="h-4 w-4 rounded text-red-800 border-gray-300 focus:ring-red-800"
                      />
                      <span className="ml-2 text-sm text-gray-500">Closed all day</span>
                    </label>
                  )}

                  {ranges.map((hour, hourIndex) => (
                    <div key={`${dayIndex}-${hourIndex}`} className="flex items-center gap-2 mt-2">
                      <HoursControl
                        value={hour}
                        onChange={(d) => changeRange(dayIndex, hourIndex, d)}
                      />
                      <button
                        type="button"
                        onClick={() => removeRange(dayIndex, hourIndex)}
                        className="bg-white px-3 pb-1 border border-gray-300 rounded-md shadow-sm text-lg font-medium text-gray-700 hover:bg-gray-50"
                      >
                        -
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </SectionEditForm>
  );
}
